import { CustomersController } from '@/shared/endpoints/customers';
import { IConvertedShipment, IDeliveryTicket, IInvoice, IPendingPrescription } from '@/shared/types/interfaces';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { getInitalStateDefField, handleApiCases, IStateDefaultField } from '@/store/helpers';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isInvoiceValid } from '../../../shared/helpers/main';
import { ICustomerRequestItem, ICustomerRequestItems } from '@/shared/types/interfaces/customer-requests';
import { StateTrigger } from '@/shared/types/enums/prescriptionState';


export interface IDashboardState {
    prescriptions: IStateDefaultField<ITransferPrescriptions[]>;
    pendingPrescriptions: IStateDefaultField<IPendingPrescription[]>;
    invoices: IStateDefaultField<IInvoice[]>;
    deliveryTickets: IStateDefaultField<IDeliveryTicket[]>;
    currentShipment: IConvertedShipment | IDeliveryTicket | null;
    requests: IStateDefaultField<ICustomerRequestItems>;
    addedRequest: IStateDefaultField<ICustomerRequestItem>;
}

export const initialState: IDashboardState = {
    prescriptions: getInitalStateDefField(),
    invoices: getInitalStateDefField(),
    deliveryTickets: getInitalStateDefField(),
    pendingPrescriptions: getInitalStateDefField(),
    currentShipment: null,
    requests: getInitalStateDefField(),
    addedRequest: getInitalStateDefField(),
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: initialState,
    reducers: {
        setCurrentShipment: (state: IDashboardState, action: PayloadAction<IConvertedShipment | IDeliveryTicket | null>) => {
            state.currentShipment = action.payload;
        },
        resetDashboardState: state => {
            Object.entries(initialState).forEach(([key, value]) => {
                state[key as keyof IDashboardState] = value;
            });
        },
    },
    extraReducers: builder => {
        handleApiCases(builder, 'prescriptions', CustomersController.getPrescriptions, {
            onFulfilled: (state, action) => {
                state.prescriptions.data = action.payload.filter(presc => {
                    return presc.stateTrigger !== StateTrigger.void;
                });
            }
        });
        handleApiCases(builder, 'invoices', CustomersController.getInvoices, {
            onFulfilled: (state, action) => {
                state.invoices.data = action.payload
                    .map(invoice => ({
                        ...invoice,
                        invoiceLineItems: invoice.invoiceLineItems.filter(item => {
                            return item.state !== 'DELETED';
                        })
                    }))
                    .filter(invoice => invoice.invoiceLineItems.length > 0 && isInvoiceValid(invoice.state));
            }
        });
        handleApiCases(builder, 'deliveryTickets', CustomersController.getDeliveryTickets, {
            onFulfilled: (state, action) => {
                const supportedTypes = new Set(['MEDBOX', 'MEDBOX-LID']);

                const filteredTickets = action.payload?.filter(ticket => {
                    const hasFill = ticket.deliveryItems?.some(item => item.fill);
                    const supplies = ticket.deliveryItems.map(item => item.supply).filter(Boolean);

                    return hasFill || supplies.every(item => item && supportedTypes.has(item?.name));
                });
                state.deliveryTickets.data = filteredTickets;
            }
        });
        handleApiCases(builder, 'pendingPrescriptions', CustomersController.getPendingPrescriptions);

        handleApiCases(builder, 'requests', CustomersController.getCustomerRequests);
        handleApiCases(builder, 'addedRequest', CustomersController.addCustomerRequest, {
            onFulfilled: (state, action) => {
                if (state.requests.data) {
                    state.requests.data.push(action.payload);
                }
            }
        });
    }
});

export const { setCurrentShipment, resetDashboardState } = dashboardSlice.actions;

export default dashboardSlice;
